import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Loader2, Wallet } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { ExportButtons } from "@/components/admin/ExportButtons";
import {
  getEarningsDashboard,
  savePayoutRecord,
  type EarningLine,
} from "@/lib/cofounder-earnings.functions";

export const Route = createFileRoute("/earnings")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Earnings — Recovery Trauma™" },
      { name: "robots", content: "noindex,nofollow" },
      { name: "description", content: "Private compensation dashboard." },
    ],
  }),
  beforeLoad: async ({ location }) => {
    if (typeof window === "undefined") return {};
    const { data, error } = await supabase.auth.getUser();
    if (!data?.user) {
      // A failed lookup (offline, slow mobile data, Web-Locks contention) is
      // not proof of being signed out. Trust a valid stored session instead
      // of bouncing a signed-in member to sign-in.
      const { readStoredAuthSession } = await import("@/lib/auth-session");
      if (error && readStoredAuthSession()?.user?.id) return {};
      throw redirect({ to: "/auth", search: { redirect: location.href } });
    }
    return {};
  },
  component: EarningsPage,
});

const money = (cents: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);

const fmtDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

function weekKey(iso: string | null) {
  if (!iso) return "Undated";
  const d = new Date(iso);
  const day = (d.getUTCDay() + 6) % 7; // Monday start
  const monday = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - day));
  return monday.toISOString().slice(0, 10);
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="rounded-2xl border border-gold/25 bg-card p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`mt-1 text-xl font-semibold ${tone ?? "text-foreground"}`}>{value}</p>
    </div>
  );
}

function EarningsPage() {
  const fetchDash = useServerFn(getEarningsDashboard);
  const saveFn = useServerFn(savePayoutRecord);
  const qc = useQueryClient();
  const [editing, setEditing] = useState<EarningLine | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["cofounder-earnings"],
    queryFn: () => fetchDash({ data: {} }),
  });

  const save = useMutation({
    mutationFn: (vars: Parameters<typeof saveFn>[0]["data"]) => saveFn({ data: vars }),
    onSuccess: () => {
      toast.success("Payment record updated");
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ["cofounder-earnings"] });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Update failed"),
  });

  const lines = useMemo(() => (data?.lines ?? []) as EarningLine[], [data]);
  const isAdmin = Boolean(data?.isAdmin);

  const payableOf = (c: EarningLine["category"]) =>
    lines.filter((l) => l.category === c && l.payable);

  const sum = (rows: EarningLine[]) => rows.reduce((s, r) => s + r.amount_cents, 0);

  const membership = payableOf("membership");
  const therapist = payableOf("therapist");
  const events = lines.filter((l) => l.category === "event");
  const payableEvents = events.filter((e) => e.payable);

  const totalEarned = sum(membership) + sum(therapist) + sum(payableEvents);
  const allPayable = [...membership, ...therapist, ...payableEvents];
  const totalPaid = sum(allPayable.filter((l) => l.paid));
  const outstanding = totalEarned - totalPaid;

  const thisMonth = (() => {
    const now = new Date();
    return sum(
      allPayable.filter((l) => {
        if (!l.occurred_at) return false;
        const d = new Date(l.occurred_at);
        return d.getUTCFullYear() === now.getUTCFullYear() && d.getUTCMonth() === now.getUTCMonth();
      }),
    );
  })();

  const weekly = useMemo(() => {
    const map = new Map<string, { membership: number; therapist: number; event: number }>();
    for (const l of allPayable) {
      const k = weekKey(l.occurred_at);
      const cur = map.get(k) ?? { membership: 0, therapist: 0, event: 0 };
      cur[l.category] += l.amount_cents;
      map.set(k, cur);
    }
    return Array.from(map.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([week, v]) => ({ week, ...v, total: v.membership + v.therapist + v.event }));
  }, [allPayable]);

  const exportCols = [
    { key: "category", header: "Category" },
    { key: "name", header: "Name" },
    { key: "email", header: "Email" },
    { key: "detail", header: "Detail" },
    { key: "gross", header: "Gross (USD)", get: (r: EarningLine) => (r.gross_cents / 100).toFixed(2) },
    { key: "share_pct", header: "Share %" },
    { key: "amount", header: "Due (USD)", get: (r: EarningLine) => (r.amount_cents / 100).toFixed(2) },
    { key: "occurred_at", header: "Date", get: (r: EarningLine) => fmtDate(r.occurred_at) },
    { key: "paid", header: "Paid", get: (r: EarningLine) => (r.paid ? "Yes" : "No") },
    { key: "paid_at", header: "Payment date", get: (r: EarningLine) => fmtDate(r.paid_at) },
    { key: "payment_method", header: "Method" },
    { key: "payment_reference", header: "Reference" },
    { key: "notes", header: "Notes" },
  ];

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-gold" />
      </div>
    );
  }

  if (error || !data?.payee) {
    return (
      <div className="mx-auto max-w-lg px-4 py-20 text-center">
        <h1 className="text-xl font-semibold">Earnings unavailable</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          This dashboard is only available to co-founders with an active compensation
          agreement.
        </p>
        <Link to="/dashboard" className="mt-6 inline-block text-sm text-gold underline">
          Back to dashboard
        </Link>
      </div>
    );
  }

  const payee = data.payee;

  const Table = ({
    title,
    rows,
    columns,
  }: {
    title: string;
    rows: EarningLine[];
    columns: { header: string; cell: (r: EarningLine) => React.ReactNode }[];
  }) => (
    <section className="mt-10">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">{title}</h2>
        <ExportButtons
          rows={rows}
          columns={exportCols}
          filename={`cofounder-${title.toLowerCase().replace(/\s+/g, "-")}`}
          sheetName={title.slice(0, 28)}
          label={title}
        />
      </div>
      <div className="overflow-x-auto rounded-2xl border border-gold/20">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-muted/40 text-start text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              {columns.map((c) => (
                <th key={c.header} className="px-3 py-2 font-medium">
                  {c.header}
                </th>
              ))}
              {isAdmin && <th className="px-3 py-2" />}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={columns.length + 1} className="px-3 py-6 text-center text-muted-foreground">
                  No records yet.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={`${r.category}:${r.source_ref}`} className="border-t border-gold/10">
                {columns.map((c) => (
                  <td key={c.header} className="px-3 py-2 align-top">
                    {c.cell(r)}
                  </td>
                ))}
                {isAdmin && (
                  <td className="px-3 py-2 text-end">
                    <button
                      type="button"
                      onClick={() => setEditing(r)}
                      className="rounded-full border border-gold/40 px-3 py-1 text-xs font-semibold hover:bg-gold/10"
                    >
                      Manage
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );

  const paidCell = (r: EarningLine) =>
    r.paid ? (
      <span className="inline-flex items-center gap-1 text-emerald-600">
        <CheckCircle2 className="h-3.5 w-3.5" /> Paid
      </span>
    ) : (
      <span className="text-amber-600">Unpaid</span>
    );

  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back
      </Link>

      <header className="mt-4 flex items-center gap-3">
        <Wallet className="h-6 w-6 text-gold" />
        <div>
          <h1 className="text-2xl font-semibold">{payee.display_name} — Earnings</h1>
          <p className="text-sm text-muted-foreground">
            {payee.membership_share_pct}% membership revenue · {payee.therapist_share_pct}% therapist
            revenue · {money(payee.event_fee_cents)} per completed event. Effective{" "}
            {fmtDate(`${payee.effective_from}T00:00:00Z`)}.
          </p>
        </div>
      </header>

      <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Membership earnings" value={money(sum(membership))} />
        <Stat label="Therapist earnings" value={money(sum(therapist))} />
        <Stat label="Event earnings" value={money(sum(payableEvents))} />
        <Stat label="Total earned (lifetime)" value={money(totalEarned)} />
        <Stat label="Total paid" value={money(totalPaid)} tone="text-emerald-600" />
        <Stat label="Outstanding balance" value={money(outstanding)} tone="text-amber-600" />
        <Stat label="This month" value={money(thisMonth)} />
        <Stat label="Records" value={String(allPayable.length)} />
      </div>

      <Table
        title="Membership revenue share"
        rows={membership}
        columns={[
          { header: "Member", cell: (r) => r.name },
          { header: "Email", cell: (r) => r.email ?? "—" },
          { header: "Provider", cell: (r) => r.detail ?? "—" },
          { header: "Membership", cell: (r) => money(r.gross_cents) },
          { header: `Commission (${payee.membership_share_pct}%)`, cell: (r) => money(r.amount_cents) },
          { header: "Payment date", cell: (r) => fmtDate(r.occurred_at) },
          { header: "Status", cell: paidCell },
        ]}
      />

      <Table
        title="Therapist revenue share"
        rows={therapist}
        columns={[
          { header: "Therapist", cell: (r) => r.name },
          { header: "Revenue type", cell: (r) => r.detail ?? "—" },
          { header: "Gross", cell: (r) => money(r.gross_cents) },
          { header: "Net eligible", cell: (r) => money(r.eligible_cents) },
          { header: `Share (${payee.therapist_share_pct}%)`, cell: (r) => money(r.amount_cents) },
          { header: "Payment date", cell: (r) => fmtDate(r.occurred_at) },
          { header: "Status", cell: paidCell },
        ]}
      />

      <Table
        title="Event payments"
        rows={events}
        columns={[
          { header: "Event", cell: (r) => r.name },
          { header: "Date", cell: (r) => fmtDate(r.occurred_at) },
          { header: "Host", cell: (r) => r.host ?? "—" },
          { header: "Event status", cell: (r) => r.event_status ?? "—" },
          { header: "Completed", cell: (r) => (r.completed ? "Yes" : "No") },
          {
            header: "Amount due",
            cell: (r) =>
              r.payable ? (
                money(r.amount_cents)
              ) : (
                <span className="text-muted-foreground">
                  {r.event_status === "cancelled" ? "Cancelled — needs approval" : "Not yet due"}
                </span>
              ),
          },
          { header: "Status", cell: (r) => (r.payable ? paidCell(r) : "—") },
          { header: "Payment date", cell: (r) => fmtDate(r.paid_at) },
          { header: "Notes", cell: (r) => r.notes ?? "—" },
        ]}
      />

      <section className="mt-10">
        <h2 className="mb-3 text-lg font-semibold">Weekly payment summary</h2>
        <div className="overflow-x-auto rounded-2xl border border-gold/20">
          <table className="w-full min-w-[560px] text-sm">
            <thead className="bg-muted/40 text-start text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">Week commencing</th>
                <th className="px-3 py-2 font-medium">Membership</th>
                <th className="px-3 py-2 font-medium">Therapist</th>
                <th className="px-3 py-2 font-medium">Events</th>
                <th className="px-3 py-2 font-medium">Total due</th>
              </tr>
            </thead>
            <tbody>
              {weekly.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                    No earnings yet.
                  </td>
                </tr>
              )}
              {weekly.map((w) => (
                <tr key={w.week} className="border-t border-gold/10">
                  <td className="px-3 py-2">{fmtDate(`${w.week}T00:00:00Z`)}</td>
                  <td className="px-3 py-2">{money(w.membership)}</td>
                  <td className="px-3 py-2">{money(w.therapist)}</td>
                  <td className="px-3 py-2">{money(w.event)}</td>
                  <td className="px-3 py-2 font-semibold">{money(w.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {isAdmin && (
        <section className="mt-10">
          <h2 className="mb-3 text-lg font-semibold">Audit trail</h2>
          <div className="max-h-80 overflow-auto rounded-2xl border border-gold/20 p-3 text-xs">
            {(data.audit ?? []).length === 0 && (
              <p className="text-muted-foreground">No payment changes recorded yet.</p>
            )}
            {(data.audit ?? []).map((a: any) => (
              <div key={a.id} className="border-b border-gold/10 py-2 last:border-0">
                <span className="font-medium">{a.action}</span>{" "}
                <span className="text-muted-foreground">
                  {new Date(a.created_at).toLocaleString("en-GB")} ·{" "}
                  {a.after_data?.category}:{a.after_data?.source_ref} ·{" "}
                  {a.after_data?.paid ? "paid" : "unpaid"}
                  {a.after_data?.payment_reference ? ` · ref ${a.after_data.payment_reference}` : ""}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {isAdmin && editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <form
            className="w-full max-w-md rounded-2xl border border-gold/30 bg-card p-5"
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget as HTMLFormElement);
              save.mutate({
                category: editing.category,
                source_ref: editing.source_ref,
                amount_cents: editing.amount_cents,
                paid: f.get("paid") === "on",
                paid_at: (f.get("paid_at") as string) || null,
                payment_method: (f.get("payment_method") as string) || null,
                payment_reference: (f.get("payment_reference") as string) || null,
                notes: (f.get("notes") as string) || null,
                approved_override: f.get("approved_override") === "on",
              });
            }}
          >
            <h3 className="text-base font-semibold">{editing.name}</h3>
            <p className="mb-4 text-xs text-muted-foreground">
              {editing.category} · {money(editing.amount_cents)} due
            </p>

            <label className="mb-3 flex items-center gap-2 text-sm">
              <input type="checkbox" name="paid" defaultChecked={editing.paid} /> Mark as paid
            </label>

            {editing.category === "event" && editing.event_status === "cancelled" && (
              <label className="mb-3 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  name="approved_override"
                  defaultChecked={editing.approved_override}
                />{" "}
                Approve payment for this cancelled event
              </label>
            )}

            <label className="mb-3 block text-sm">
              Payment date
              <input
                type="date"
                name="paid_at"
                defaultValue={editing.paid_at ? editing.paid_at.slice(0, 10) : ""}
                className="mt-1 w-full rounded-lg border border-gold/30 bg-background px-3 py-2"
              />
            </label>
            <label className="mb-3 block text-sm">
              Payment method
              <input
                name="payment_method"
                defaultValue={editing.payment_method ?? ""}
                className="mt-1 w-full rounded-lg border border-gold/30 bg-background px-3 py-2"
              />
            </label>
            <label className="mb-3 block text-sm">
              Payment reference
              <input
                name="payment_reference"
                defaultValue={editing.payment_reference ?? ""}
                className="mt-1 w-full rounded-lg border border-gold/30 bg-background px-3 py-2"
              />
            </label>
            <label className="mb-4 block text-sm">
              Notes
              <textarea
                name="notes"
                defaultValue={editing.notes ?? ""}
                rows={3}
                className="mt-1 w-full rounded-lg border border-gold/30 bg-background px-3 py-2"
              />
            </label>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="rounded-full border border-gold/30 px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={save.isPending}
                className="rounded-full bg-gold px-4 py-2 text-sm font-semibold text-background disabled:opacity-60"
              >
                {save.isPending ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
